import React, {
  forwardRef,
  useImperativeHandle,
  useState,
  ForwardRefRenderFunction,
  ReactElement,
} from 'react';
import { Radio } from 'antd';
import { useDispatch } from 'react-redux';
import { endOfDay, format, isValid, startOfDay } from 'date-fns';
import {
  setIsOnRequestReport,
  setReportingDateRange,
  setReportingDateRangeApplied,
} from '../../../store/actions/reportingAndAnalyticsActions';
import ReportingDatePicker from '../../../shared/components/crmFormElement/FormInputDate/ReportingDatePicker';
import { ReactComponent as CalendarIcon } from '../../../../src/assets/images/svg/CalendarIcon.svg';
import '../ReportingAndAnalytics.less';
import { ISO_8601_EXTENDED_FORMAT } from '../../../shared/constants/commonConstants';
import { ReactComponent as DownOutlined } from '../../../assets/images/svg/ReportingDownChevronUnselected.svg';
import { ReactComponent as UpOutlined } from '../../../assets/images/svg/ReportingUpChevronSelected.svg';
import { ReactComponent as RangePickerArrow } from '../../../assets/images/svg/RangePickerArrow.svg';
import { REPORTING_CONSTANTS } from './reportingAndAnalyticsConstants';
import { isValidRole } from '../../../utils/utility';
import {
  parseReportingDisplayDate,
} from '../../../utils/reportingUtility';
import { GenericExpandableRadioFilterProps, GenericExpandableRadioFilterRef } from '../../../shared/interfaces/reportingAndAnalyticsInterface';

const GenericExpandableRadioFilter: ForwardRefRenderFunction<
  GenericExpandableRadioFilterRef,
  GenericExpandableRadioFilterProps
> = (
  { label, options, onChange, defaultSelected = '' }: any,
  ref: any,
): ReactElement => {
  const dispatch = useDispatch();
  const [selectedValue, setSelectedValue] = useState<string>(defaultSelected);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [startDate, setStartDate] = useState<Date | undefined>();
  const [endDate, setEndDate] = useState<Date | undefined>();
  const [rangeSelectorSelected, setRangeSelectorSelected] =
    useState<boolean>(false);
  const reportingWriteAccess = isValidRole(
    REPORTING_CONSTANTS.REPORTING_WRITE_ACCESS,
  );

  const handleRadioChange = (element: any): void => {
    const value = element?.target?.value;
    setSelectedValue(value);
    onChange(value);
    dispatch(setReportingDateRange({}));
    dispatch(setReportingDateRangeApplied(false));
    setStartDate(undefined);
    setEndDate(undefined);
    setIsDatePickerOpen(false);
    setRangeSelectorSelected(false);
  };

  const resetFilter = (): void => {
    setSelectedValue('');
    setIsExpanded(false);
    dispatch(setReportingDateRange({}));
    setStartDate(undefined);
    setEndDate(undefined);
    setIsDatePickerOpen(false);
    dispatch(setReportingDateRangeApplied(false));
    setRangeSelectorSelected(false);
  };

  const handleDateRangeSelect = (startStr: string, endStr: string): void => {
    const start = parseReportingDisplayDate(startStr);
    const end = parseReportingDisplayDate(endStr);
      setStartDate(start);
      setEndDate(end);
    dispatch(
      setReportingDateRange({
        startDate: start ? format(startOfDay(start), ISO_8601_EXTENDED_FORMAT) : '',
        endDate: end ? format(endOfDay(end), ISO_8601_EXTENDED_FORMAT) : '',
      }),
    );
  };

  const handleDateOk = (): void => {
    onChange('0');
    setSelectedValue('');
    setRangeSelectorSelected(true);
  };

  const formattedStartDate =
    startDate && isValid(startDate)
      ? format(startDate, REPORTING_CONSTANTS.REPORTING_DISPLAY_FORMAT)
      : '';

  const formattedEndDate =
    endDate && isValid(endDate)
      ? format(endDate, REPORTING_CONSTANTS.REPORTING_DISPLAY_FORMAT)
      : '';

  useImperativeHandle(ref, (): any => ({
    // to call reset the filter from parent component
    resetFilter,
  }));

  return (
    <div className="expandable-filter-container">
      <div
        className="expandable-filter-header reportingRadioSelect"
        onClick={(): void => setIsExpanded((prev: boolean): boolean => !prev)}
      >
        <Radio
          checked={!!selectedValue || rangeSelectorSelected}
          onClick={(e: React.MouseEvent<HTMLDivElement>): void =>
            e.stopPropagation()
          }
          className="non-clickable-radio"
        />
        <span className={`filter-label ${selectedValue && 'selected-label'}`}>
          {label}
        </span>
        {isExpanded ? <UpOutlined /> : <DownOutlined />}
      </div>

      {isExpanded && (
        <div className="expandable-radio-filter-content reportingRadioSelect">
          <Radio.Group
            onChange={handleRadioChange}
            value={selectedValue}
            className="radio-group"
          >
            {options?.map((option: any): ReactElement => (
              <Radio
                key={option?.value}
                value={option?.value}
                className="radio-item"
              >
                {option?.label}
              </Radio>
            ))}
          </Radio.Group>
          <div className="compact-range-wrapper">
            <div
              className="range-input"
              onClick={(): void => setIsDatePickerOpen(!isDatePickerOpen)}
            >
              <div className="field">
                {formattedStartDate || (
                  <span className="placeholder">
                    {REPORTING_CONSTANTS.REPORTING_PLACEHOLDER}
                  </span>
                )}
              </div>
              <div className="arrow">
                <RangePickerArrow />
              </div>
              <div className="field">
                {formattedEndDate || (
                  <span className="placeholder">
                    {REPORTING_CONSTANTS.REPORTING_PLACEHOLDER}
                  </span>
                )}
              </div>
              <CalendarIcon />
            </div>

            {isDatePickerOpen && (
              <ReportingDatePicker
                initialStartDate={startDate}
                initialEndDate={endDate}
                limit={180}
                leftLimit={90}
                rightLimit={30}
                disablefutureDates={true}
                disabledStartDate={false}
                setIsDatePickerOpen={setIsDatePickerOpen}
                handleDateOk={handleDateOk}
                setSelect={handleDateRangeSelect}
              />
            )}
          </div>
          {reportingWriteAccess && (
            <div
              className="request-report-link"
              onClick={(): void => {
                dispatch(setIsOnRequestReport(true));
              }}
            >
              {REPORTING_CONSTANTS.ON_REQUEST_REPORT}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default forwardRef(GenericExpandableRadioFilter);
